/**
 * Arrears, expiries and late fees — the arithmetic behind the dashboard traffic light.
 *
 * The product's whole promise is that nobody finds out about an arrear or an expiry after the
 * fact, so the classification rules live here in one pure module rather than being re-derived
 * in each query. Every screen, every report and every reminder uses the same buckets, which is
 * why the dashboard total always equals the list it links to.
 */

import { addDays, diffDays, type PlainDate } from "./dates";
import { balanceOf, isSettled, type InvoiceLike } from "./billing";
import { applyRate, sum, type BasisPoints, type Minor } from "./money";

/** How far ahead "due soon" looks, unless the agency has changed it. */
export const DEFAULT_DUE_SOON_DAYS = 30;

export type MoneyBucket = "overdue" | "due_soon" | "upcoming" | "settled";

export function classifyInvoice(
  invoice: Pick<InvoiceLike, "dueDate" | "total" | "paid" | "status">,
  asOf: PlainDate,
  opts: { graceDays?: number; dueSoonDays?: number } = {},
): MoneyBucket {
  const { graceDays = 0, dueSoonDays = DEFAULT_DUE_SOON_DAYS } = opts;
  if (isSettled(invoice.status) || balanceOf(invoice) <= 0) return "settled";
  const lateFrom = addDays(invoice.dueDate, graceDays);
  if (asOf > lateFrom) return "overdue";
  if (diffDays(asOf, invoice.dueDate) <= dueSoonDays) return "due_soon";
  return "upcoming";
}

/** Days past due. Zero while the invoice is still current — never negative. */
export function daysOverdue(dueDate: PlainDate, asOf: PlainDate, graceDays = 0): number {
  return Math.max(0, diffDays(addDays(dueDate, graceDays), asOf));
}

export type AgeingBucket = "current" | "d1_30" | "d31_60" | "d61_90" | "d90_plus";

export const AGEING_BUCKETS: Array<{ key: AgeingBucket; label: string }> = [
  { key: "current", label: "Current" },
  { key: "d1_30", label: "1–30 days" },
  { key: "d31_60", label: "31–60 days" },
  { key: "d61_90", label: "61–90 days" },
  { key: "d90_plus", label: "90+ days" },
];

export function ageingBucket(dueDate: PlainDate, asOf: PlainDate, graceDays = 0): AgeingBucket {
  const days = daysOverdue(dueDate, asOf, graceDays);
  if (days <= 0) return "current";
  if (days <= 30) return "d1_30";
  if (days <= 60) return "d31_60";
  if (days <= 90) return "d61_90";
  return "d90_plus";
}

export interface AgeingRow {
  key: string;
  label: string;
  buckets: Record<AgeingBucket, Minor>;
  total: Minor;
}

/** The arrears ageing report: outstanding balance per debtor, split by how old the debt is. */
export function ageingReport<T extends { dueDate: PlainDate; total: Minor; paid: Minor; status: InvoiceLike["status"] }>(
  invoices: Array<T & { groupKey: string; groupLabel: string }>,
  asOf: PlainDate,
  graceDays = 0,
): AgeingRow[] {
  const rows = new Map<string, AgeingRow>();
  for (const invoice of invoices) {
    const balance = balanceOf(invoice);
    if (isSettled(invoice.status) || balance <= 0) continue;
    let row = rows.get(invoice.groupKey);
    if (!row) {
      row = {
        key: invoice.groupKey,
        label: invoice.groupLabel,
        buckets: { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_plus: 0 },
        total: 0,
      };
      rows.set(invoice.groupKey, row);
    }
    row.buckets[ageingBucket(invoice.dueDate, asOf, graceDays)] += balance;
    row.total += balance;
  }
  return [...rows.values()].sort((a, b) => b.total - a.total);
}

export interface MoneySummary {
  overdue: { count: number; amount: Minor };
  dueSoon: { count: number; amount: Minor };
  upcoming: { count: number; amount: Minor };
  /** Every unsettled balance, whenever it falls due. */
  outstanding: Minor;
}

export function summariseMoney(
  invoices: Array<Pick<InvoiceLike, "dueDate" | "total" | "paid" | "status">>,
  asOf: PlainDate,
  opts: { graceDays?: number; dueSoonDays?: number } = {},
): MoneySummary {
  const summary: MoneySummary = {
    overdue: { count: 0, amount: 0 },
    dueSoon: { count: 0, amount: 0 },
    upcoming: { count: 0, amount: 0 },
    outstanding: 0,
  };
  for (const invoice of invoices) {
    const bucket = classifyInvoice(invoice, asOf, opts);
    if (bucket === "settled") continue;
    const balance = balanceOf(invoice);
    summary.outstanding += balance;
    if (bucket === "overdue") {
      summary.overdue.count += 1;
      summary.overdue.amount += balance;
    } else if (bucket === "due_soon") {
      summary.dueSoon.count += 1;
      summary.dueSoon.amount += balance;
    } else {
      summary.upcoming.count += 1;
      summary.upcoming.amount += balance;
    }
  }
  return summary;
}

/* ------------------------------------------------------------------ expiries */

export type ExpiryBucket = "expired" | "d0_30" | "d31_60" | "d61_90" | "later";

export const EXPIRY_BUCKETS: Array<{ key: ExpiryBucket; label: string; tone: "danger" | "warn" | "info" }> = [
  { key: "expired", label: "Already expired", tone: "danger" },
  { key: "d0_30", label: "Next 30 days", tone: "danger" },
  { key: "d31_60", label: "31–60 days", tone: "warn" },
  { key: "d61_90", label: "61–90 days", tone: "info" },
  { key: "later", label: "Later", tone: "info" },
];

export function expiryBucket(end: PlainDate, asOf: PlainDate): ExpiryBucket {
  const days = diffDays(asOf, end);
  if (days < 0) return "expired";
  if (days <= 30) return "d0_30";
  if (days <= 60) return "d31_60";
  if (days <= 90) return "d61_90";
  return "later";
}

export function summariseExpiries<T extends { endDate: PlainDate | null }>(
  leases: T[],
  asOf: PlainDate,
): Record<ExpiryBucket, T[]> {
  const out: Record<ExpiryBucket, T[]> = { expired: [], d0_30: [], d31_60: [], d61_90: [], later: [] };
  for (const lease of leases) {
    if (!lease.endDate) continue; // open-ended leases cannot expire
    out[expiryBucket(lease.endDate, asOf)].push(lease);
  }
  return out;
}

/* ------------------------------------------------------------------ late fees */

export interface LateFeeRule {
  enabled: boolean;
  basis: "fixed" | "percent_of_balance";
  /** Minor units, or basis points when the basis is a percentage. */
  value: number;
  /** Days after the due date before any fee applies. */
  graceDays: number;
  /** `once` charges a single fee; `per_period` charges every `periodDays` while unpaid. */
  frequency: "once" | "per_period";
  periodDays: number;
  /** Ceiling on the total fee for one invoice. Zero means uncapped. */
  cap: Minor;
}

export const DEFAULT_LATE_FEE_RULE: LateFeeRule = {
  enabled: false, // off by default: a fee nobody agreed to is a fee nobody pays
  basis: "percent_of_balance",
  value: 500, // 5%
  graceDays: 7,
  frequency: "once",
  periodDays: 30,
  cap: 0,
};

/**
 * What late fee an overdue invoice has earned by `asOf`, given what has already been charged.
 *
 * Returns the *incremental* amount to raise, so running this daily is safe and idempotent —
 * the job can be re-run after a failure without double-charging anyone.
 */
export function lateFeeDue(
  invoice: { dueDate: PlainDate; total: Minor; paid: Minor; status: InvoiceLike["status"] },
  rule: LateFeeRule,
  asOf: PlainDate,
  alreadyCharged: Minor = 0,
): Minor {
  if (!rule.enabled) return 0;
  if (isSettled(invoice.status)) return 0;
  const balance = balanceOf(invoice);
  if (balance <= 0) return 0;

  const overdue = daysOverdue(invoice.dueDate, asOf, rule.graceDays);
  if (overdue <= 0) return 0;

  const unit = rule.basis === "percent_of_balance" ? applyRate(balance, rule.value as BasisPoints) : rule.value;
  const times = rule.frequency === "once" ? 1 : Math.max(1, Math.ceil(overdue / Math.max(1, rule.periodDays)));
  let target = unit * times;
  if (rule.cap > 0) target = Math.min(target, rule.cap);

  return Math.max(0, target - alreadyCharged);
}

/* ------------------------------------------------------------------ collections */

export interface CollectionPoint {
  key: string;
  label: string;
  billed: Minor;
  collected: Minor;
}

/** Collection rate as a percentage, guarding the divide-by-zero on a month with nothing billed. */
export function collectionRate(points: CollectionPoint[]): number {
  const billed = sum(points.map((p) => p.billed));
  if (billed <= 0) return 0;
  return Math.round((sum(points.map((p) => p.collected)) / billed) * 100);
}

/**
 * A tenant's payment reliability, from their settled invoices. Shown on the client record so an
 * agent renewing a tenancy knows who they are dealing with without reading twelve rows.
 */
export function reliability(
  history: Array<{ dueDate: PlainDate; settledOn: PlainDate | null }>,
  asOf: PlainDate,
  graceDays = 0,
): { onTime: number; late: number; outstanding: number; score: number; label: string } {
  let onTime = 0;
  let late = 0;
  let outstanding = 0;
  for (const row of history) {
    if (!row.settledOn) {
      if (asOf > addDays(row.dueDate, graceDays)) outstanding += 1;
      continue;
    }
    if (row.settledOn <= addDays(row.dueDate, graceDays)) onTime += 1;
    else late += 1;
  }
  const considered = onTime + late + outstanding;
  const score = considered === 0 ? 100 : Math.round(((onTime + late * 0.4) / considered) * 100);
  const label = considered === 0 ? "No history" : score >= 90 ? "Excellent" : score >= 70 ? "Good" : score >= 45 ? "Watch" : "At risk";
  return { onTime, late, outstanding, score, label };
}
